import type { Role } from '@sentra/contracts';
import { translate } from '@sentra/contracts';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { roleLabelKey } from '@/app/shell/header';
import { useLocale } from '@/i18n';

/**
 * Why this role cannot open the screen a URL reached, in the server's own sentence.
 *
 * **The sentence is `AUTHZ_ROLE_FORBIDDEN`'s**, from the contracts catalogue rather than a second
 * one written for the browser: what the screen would have been refused with at its first request is
 * what it says instead of mounting. The role is named in the header's words, so the refusal and the
 * label beside the operator's name cannot disagree about who is signed in.
 *
 * `data-testid="screen-forbidden"` is the administration section's id for the same refusal, so a
 * test does not have to know which of the two layers caught a path.
 */
export function ScreenRefusal({ role }: Readonly<{ role: Role }>): ReactNode {
  const { t } = useTranslation();
  const { locale } = useLocale();

  return (
    <p className="text-sm text-muted-foreground" data-testid="screen-forbidden">
      {translate(locale, 'errors.authz.role_forbidden', { role: t(roleLabelKey(role)) })}
    </p>
  );
}
